import { XIcon } from "@phosphor-icons/react";
import {
  AttachmentBubbleCsv,
  AttachmentBubbleImage,
  AttachmentBubblePdf,
} from "@/components/ui/chat/AttachmentBubbles";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

const CHAT_ENCODED_REQUEST_LIMIT_HEADLINE =
  "Message and attachments exceed the 20 MB send limit.";

export const attachmentFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const isImageAttachmentFile = (file: File) =>
  file.type.startsWith("image/") || /\.(png|jpe?g)$/i.test(file.name);

export interface ChatInputAttachmentListProps {
  files: FileList;
  allowImages?: boolean;
  imagePreviewUrls: string[];
  attachmentsWithinLimit: boolean;
  onRemoveFile: (file?: File) => void;
  className?: string;
}

export const ChatInputAttachmentList = ({
  files,
  allowImages = false,
  imagePreviewUrls,
  attachmentsWithinLimit,
  onRemoveFile,
  className,
}: ChatInputAttachmentListProps) => (
  <div className={cn("flex flex-col gap-1.5 pt-3", className)}>
    {!attachmentsWithinLimit ? (
      <p className="text-xs text-amber-700" role="alert">
        {CHAT_ENCODED_REQUEST_LIMIT_HEADLINE} Remove a file or shorten your
        message to send.
      </p>
    ) : null}
    <div className="flex flex-wrap gap-1.5">
      {Array.from(files).map((file, fileIndex) => {
        if (file.name.endsWith(".csv") || file.type === "text/csv") {
          return (
            <AttachmentBubbleCsv
              key={attachmentFileKey(file)}
              filename={file.name}
              onRemove={() => onRemoveFile(file)}
              variant="input"
            />
          );
        }

        if (allowImages && isImageAttachmentFile(file)) {
          return (
            <div
              key={attachmentFileKey(file)}
              className="relative flex shrink-0 items-start"
            >
              <AttachmentBubbleImage
                src={imagePreviewUrls[fileIndex] || undefined}
                alt={file.name}
              />
              <IconButton
                icon={<XIcon weight="regular" />}
                variant="ghost"
                size="sm"
                aria-label={`Remove ${file.name}`}
                tooltipLabel="Remove file"
                tooltipPosition="right"
                onPress={() => onRemoveFile(file)}
                className="absolute -right-1 -top-1 size-5 rounded-full bg-white shadow-xs"
              />
            </div>
          );
        }

        return (
          <AttachmentBubblePdf
            key={attachmentFileKey(file)}
            filename={file.name}
            onRemove={() => onRemoveFile(file)}
            variant="input"
          />
        );
      })}
    </div>
  </div>
);
